/*
 * GAN Loop: teaching simulator (Case Study, HL only)
 * ------------------------------------------------------------------------------
 * WHAT IT TEACHES
 *   A GAN (generative adversarial network) is two networks in competition:
 *     • Generator: turns a D-dimensional noise vector into a candidate (fake)
 *       image. It never sees the real images directly; it learns only from the
 *       discriminator's verdict.
 *     • Discriminator: compares candidates against real images and judges each
 *       one real or fake.
 *   The adversarial dynamic drives sharp images, and the BALANCE between the two
 *   networks is the condition for good results. Unbalance them and it breaks:
 *     • Generator too strong  -> mode collapse (a few near-identical outputs;
 *       training is still running, it has just lost diversity).
 *     • Discriminator too strong -> training stalled (the generator stops
 *       improving, so realism plateaus).
 *
 * This is an ILLUSTRATION, not real machine learning. Nothing is trained, no
 * dataset is loaded, no library is used. "Images" are simple coloured shapes;
 * the realism and variety meters are computed from the slider balance by simple
 * rules. Do not read any real GAN behaviour into the numbers.
 *
 * CONTROLS
 *   • Generator strength (slider 0-100) ..... push up to over-power the pair.
 *   • Discriminator strength (slider 0-100) . push up to stall the generator.
 *   • Train one round (button) .............. advance the adversarial loop once.
 *   • Play / Pause (button) ................. auto-run rounds (disabled when the
 *                                             browser requests reduced motion;
 *                                             train manually instead).
 *   • Reset (button) ........................ return to a neutral start.
 *   • Readouts .............................. round counter, realism meter,
 *                                             variety meter, status banner.
 *   • Fake thumbnails ....................... a grid of the current fakes,
 *                                             refreshed each round.
 *   • Explainer accordion ................... how a GAN trains.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';

// ── brookebit palette (shared with the other simulators) ───────────────────────
const B = {
  bg:           '#faf7f2',
  surface:      '#ffffff',
  border:       'rgba(60,45,30,0.10)',
  borderStrong: 'rgba(60,45,30,0.16)',
  text:         '#2a241d',
  muted:        '#6b6055',
  faint:        '#9a8f82',
  green:        '#7BC19E',
  greenDark:    '#5fa882',
  greenSoft:    '#eef6f1',
  charcoal:     '#3A3D3C',
  blue:         '#5a7abf',
  blueDark:     '#3d5c9e',
  amber:        '#f0c74e',
  amberDark:    '#c9a12e',
  purple:       '#7a5bbf',
  purpleSoft:   '#efe7fb',
  red:          '#c76a5a',
};

const font = "'Nunito', -apple-system, sans-serif";
const mono = "'JetBrains Mono', 'Fira Mono', monospace";

const ROUND_CAP = 30;        // auto-pause the play loop after this many rounds
const N_THUMBS  = 6;

// Deterministic pseudo-random so a given round renders identically every time.
const seeded = (n) => {
  const x = Math.sin(n * 127.1 + 31.7) * 43758.5453;
  return x - Math.floor(x);
};

// ── Regime + targets from the slider balance ───────────────────────────────────
// balance = generator strength − discriminator strength.
function regimeOf(gen, disc) {
  const balance = gen - disc;
  if (balance > 20)  return 'collapse';   // generator over-powers the pair
  if (balance < -20) return 'stalled';    // discriminator over-powers the pair
  return 'balanced';
}
// Where realism / variety ease toward, per regime.
function targetsFor(regime) {
  if (regime === 'collapse') return { realism: 80, variety: 8 };   // sharp but identical
  if (regime === 'stalled')  return { realism: 30, variety: 45 };  // realism stuck low
  return { realism: 93, variety: 92 };                             // both climb
}

const NEUTRAL = { realism: 12, variety: 55 };  // pre-training: noisy + fairly random

// ── One fake "image": a coloured shape ─────────────────────────────────────────
const SHAPES = ['circle', 'square', 'triangle', 'hexagon', 'star', 'drop'];

function shapeEl(shape, fill, stroke) {
  const common = { fill, stroke, strokeWidth: 1.4, strokeLinejoin: 'round' };
  switch (shape) {
    case 'square':   return <rect x="14" y="14" width="36" height="36" rx="6" {...common} />;
    case 'triangle': return <polygon points="32,11 53,52 11,52" {...common} />;
    case 'hexagon':  return <polygon points="32,10 52,21 52,43 32,54 12,43 12,21" {...common} />;
    case 'star':     return <polygon points="32,9 38,26 56,26 41,37 47,54 32,43 17,54 23,37 8,26 26,26" {...common} />;
    case 'drop':     return <path d="M32 10 C46 30 48 40 40 48 C33 55 31 55 24 48 C16 40 18 30 32 10 Z" {...common} />;
    case 'circle':
    default:         return <circle cx="32" cy="32" r="20" {...common} />;
  }
}

function FakeThumb({ hue, shape, realism, seed, reduceMotion }) {
  const r = realism / 100;
  const sat = Math.round(16 + r * 60);           // low realism -> washed out
  const gid = `gan-grad-${seed}`;
  const fill = `url(#${gid})`;
  const stroke = `hsl(${hue}, ${sat}%, 34%)`;

  // Grain overlay: dense when realism is low, invisible when high
  const alpha = (1 - r) * 0.6;
  const specks = [];
  if (alpha > 0.02) {
    for (let i = 0; i < 14; i++) {
      specks.push(
        <rect key={i}
          x={(seeded(seed * 13 + i) * 58 + 3).toFixed(1)}
          y={(seeded(seed * 7 + i * 3) * 58 + 3).toFixed(1)}
          width="3.2" height="3.2"
          fill={i % 2 ? '#111' : '#fff'} opacity={alpha.toFixed(2)} />
      );
    }
  }

  return (
    <svg viewBox="0 0 64 64" style={{
      width: '100%', height: 'auto', display: 'block', borderRadius: 8,
      border: `1px solid ${B.border}`,
      transition: reduceMotion ? 'none' : 'opacity 0.2s',
    }} aria-hidden="true">
      <defs>
        <radialGradient id={gid} cx="40%" cy="34%" r="70%">
          <stop offset="0%"  stopColor={`hsl(${hue}, ${sat}%, 68%)`} />
          <stop offset="100%" stopColor={`hsl(${hue}, ${sat}%, 44%)`} />
        </radialGradient>
      </defs>
      <rect width="64" height="64" fill={B.bg} />
      {shapeEl(shape, fill, stroke)}
      {specks}
    </svg>
  );
}

// Build the current fake set. Variety spreads hue + shape across the grid;
// low variety collapses every thumbnail onto one "mode".
function computeFakes(round, variety) {
  const v = variety / 100;
  const baseHue = (round * 37) % 360;
  const baseShape = round % SHAPES.length;
  const out = [];
  for (let i = 0; i < N_THUMBS; i++) {
    const seed = round * 100 + i + 1;
    const varied = seeded(seed * 3) < v;                       // more variety -> more varied
    const shapeIdx = varied ? (baseShape + i) % SHAPES.length : baseShape;
    const hueJit = (seeded(seed * 5) - 0.5) * 40 * v;
    const hue = (baseHue + (varied ? (i - 2.5) * 26 * v : 0) + hueJit + 360) % 360;
    out.push({ hue: Math.round(hue), shape: SHAPES[shapeIdx], seed });
  }
  return out;
}

// ── The adversarial-loop diagram (mirrors the eBook figure) ────────────────────
function LoopDiagram({ running, reduceMotion }) {
  const box = (x, y, w, h, fill, stroke) => (
    <rect x={x} y={y} width={w} height={h} rx="9" fill={fill} stroke={stroke} strokeWidth="1.6" />
  );
  const dash = running && !reduceMotion;
  return (
    <svg viewBox="0 0 720 250" style={{ width: '100%', height: 'auto', display: 'block' }}
      role="img" aria-label="GAN loop: a D-dimensional noise vector feeds the generator, which makes fake images; fake and real images feed the discriminator, which outputs a real or fake verdict that the generator learns from.">
      <defs>
        <marker id="gan-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
          <path d="M0 0 L10 5 L0 10 z" fill={B.charcoal} />
        </marker>
        <marker id="gan-arrow-amber" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
          <path d="M0 0 L10 5 L0 10 z" fill={B.amberDark} />
        </marker>
        <style>{`@keyframes ganflow { to { stroke-dashoffset: -16; } }`}</style>
      </defs>

      {/* Noise vector */}
      {box(14, 92, 108, 56, B.bg, B.borderStrong)}
      <text x="68" y="114" textAnchor="middle" fontSize="12" fontWeight="700" fill={B.text}>D-dimensional</text>
      <text x="68" y="130" textAnchor="middle" fontSize="12" fontWeight="700" fill={B.text}>noise vector</text>

      {/* Generator */}
      {box(170, 90, 120, 60, B.greenSoft, B.greenDark)}
      <text x="230" y="115" textAnchor="middle" fontSize="14" fontWeight="800" fill={B.greenDark}>Generator</text>
      <text x="230" y="132" textAnchor="middle" fontSize="10.5" fill={B.muted}>makes fakes</text>

      {/* Fake / real images */}
      {box(338, 34, 116, 56, B.surface, B.borderStrong)}
      <text x="396" y="58" textAnchor="middle" fontSize="12.5" fontWeight="700" fill={B.text}>Fake images</text>
      <text x="396" y="74" textAnchor="middle" fontSize="10.5" fill={B.faint}>from generator</text>
      {box(338, 150, 116, 56, B.surface, B.borderStrong)}
      <text x="396" y="174" textAnchor="middle" fontSize="12.5" fontWeight="700" fill={B.text}>Real images</text>
      <text x="396" y="190" textAnchor="middle" fontSize="10.5" fill={B.faint}>training set</text>

      {/* Discriminator */}
      {box(502, 92, 122, 56, B.purpleSoft, B.purple)}
      <text x="563" y="116" textAnchor="middle" fontSize="14" fontWeight="800" fill={B.purple}>Discriminator</text>
      <text x="563" y="133" textAnchor="middle" fontSize="10.5" fill={B.muted}>judges each</text>

      {/* Verdict */}
      <text x="646" y="116" fontSize="12.5" fontWeight="800" fill={B.text}>Real /</text>
      <text x="646" y="132" fontSize="12.5" fontWeight="800" fill={B.text}>Fake</text>

      {/* Arrows (forward) */}
      <line x1="122" y1="120" x2="166" y2="120" stroke={B.charcoal} strokeWidth="2" markerEnd="url(#gan-arrow)" />
      <line x1="290" y1="112" x2="334" y2="72"  stroke={B.charcoal} strokeWidth="2" markerEnd="url(#gan-arrow)" />
      <line x1="454" y1="66"  x2="500" y2="104" stroke={B.charcoal} strokeWidth="2" markerEnd="url(#gan-arrow)" />
      <line x1="454" y1="176" x2="500" y2="136" stroke={B.charcoal} strokeWidth="2" markerEnd="url(#gan-arrow)" />
      <line x1="624" y1="120" x2="640" y2="120" stroke={B.charcoal} strokeWidth="2" markerEnd="url(#gan-arrow)" />

      {/* Feedback arrow: generator learns only from the verdict */}
      <path d="M560 148 C560 232 230 232 230 152" fill="none" stroke={B.amberDark} strokeWidth="2"
        strokeDasharray="7 5" markerEnd="url(#gan-arrow-amber)"
        style={dash ? { animation: 'ganflow 0.7s linear infinite' } : undefined} />
      <text x="395" y="243" textAnchor="middle" fontSize="11" fontWeight="700" fill={B.amberDark}>
        generator learns only from the verdict (adversarial dynamic)
      </text>
    </svg>
  );
}

// ── Meter ──────────────────────────────────────────────────────────────────────
function Meter({ label, value, color, sub, reduceMotion }) {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span style={{ fontSize: '0.78rem', fontWeight: 700, color: B.text }}>{label}</span>
        <span style={{ fontFamily: mono, fontSize: '0.9rem', fontWeight: 800, color: B.text }}>{Math.round(value)}%</span>
      </div>
      <div style={{ height: 10, borderRadius: 6, background: B.bg, border: `1px solid ${B.border}`, overflow: 'hidden', marginTop: 5 }}>
        <div style={{ width: `${Math.max(0, Math.min(100, value))}%`, height: '100%', background: color, transition: reduceMotion ? 'none' : 'width 0.3s' }} />
      </div>
      <div style={{ fontSize: '0.7rem', color: B.faint, marginTop: 4, lineHeight: 1.4 }}>{sub}</div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function GANLoopSimulator({ onBack }) {
  const [gen,  setGen]  = useState(50);
  const [disc, setDisc] = useState(50);
  const [round, setRound] = useState(0);
  const [realism, setRealism] = useState(NEUTRAL.realism);
  const [variety, setVariety] = useState(NEUTRAL.variety);
  const [playing, setPlaying] = useState(false);
  const [hintOpen, setHintOpen] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);

  // Refs mirror state so the play loop reads live values, not stale closures.
  const genRef = useRef(50), discRef = useRef(50);
  const roundRef = useRef(0), realismRef = useRef(NEUTRAL.realism), varietyRef = useRef(NEUTRAL.variety);

  // ── Reduced-motion preference ────────────────────────────────────────────────
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => setReduceMotion(mq.matches);
    apply();
    mq.addEventListener?.('change', apply);
    return () => mq.removeEventListener?.('change', apply);
  }, []);

  // ── One training round: ease the meters toward the current regime's targets ──
  const applyRound = useCallback(() => {
    roundRef.current += 1;
    const regime = regimeOf(genRef.current, discRef.current);
    const t = targetsFor(regime);
    realismRef.current += (t.realism - realismRef.current) * 0.28;
    varietyRef.current += (t.variety - varietyRef.current) * 0.28;
    setRound(roundRef.current);
    setRealism(realismRef.current);
    setVariety(varietyRef.current);
    return roundRef.current;
  }, []);

  // Auto-run loop
  useEffect(() => {
    if (!playing) return undefined;
    if (roundRef.current >= ROUND_CAP) { setPlaying(false); return undefined; }
    const id = setInterval(() => {
      const r = applyRound();
      if (r >= ROUND_CAP) setPlaying(false);
    }, 550);
    return () => clearInterval(id);
  }, [playing, applyRound]);

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleGen = useCallback((v) => { genRef.current = v; setGen(v); }, []);
  const handleDisc = useCallback((v) => { discRef.current = v; setDisc(v); }, []);
  const handleTrain = useCallback(() => { if (roundRef.current < ROUND_CAP) applyRound(); }, [applyRound]);
  const handlePlayPause = useCallback(() => {
    if (reduceMotion) return;
    setPlaying(p => {
      if (!p && roundRef.current >= ROUND_CAP) return false;
      return !p;
    });
  }, [reduceMotion]);
  const handleReset = useCallback(() => {
    roundRef.current = 0;
    realismRef.current = NEUTRAL.realism;
    varietyRef.current = NEUTRAL.variety;
    genRef.current = 50; discRef.current = 50;
    setRound(0); setRealism(NEUTRAL.realism); setVariety(NEUTRAL.variety);
    setGen(50); setDisc(50); setPlaying(false);
  }, []);

  const regime = regimeOf(gen, disc);
  const capped = round >= ROUND_CAP;
  const fakes = useMemo(() => computeFakes(round, variety), [round, variety]);

  // Status banner content
  const status = (() => {
    if (round === 0) return { tone: 'neutral', title: 'Ready', text: 'Press Train one round (or Play) to run the adversarial loop.' };
    if (regime === 'collapse') return {
      tone: 'bad', title: 'Mode collapse',
      text: 'The generator over-powers the discriminator and has settled on a few near-identical outputs. Training is still running; it has just lost diversity.',
    };
    if (regime === 'stalled') return {
      tone: 'warn', title: 'Training stalled',
      text: 'The discriminator over-powers the generator, which stops improving, so realism plateaus and never becomes sharp.',
    };
    return {
      tone: 'good', title: 'Balanced adversarial dynamic',
      text: 'The two networks are matched, so the competition drives realism and variety upward together, which is the condition for good results.',
    };
  })();
  const toneColor = { neutral: B.faint, good: B.greenDark, warn: B.amberDark, bad: B.red }[status.tone];
  const toneBg    = { neutral: B.bg, good: B.greenSoft, warn: '#fbf3dd', bad: '#f7e7e2' }[status.tone];

  // ── Styles (shared vocabulary with the other simulators) ─────────────────────
  const s = {
    wrap: { fontFamily: font, background: B.bg, minHeight: '100vh', WebkitFontSmoothing: 'antialiased' },
    topbar: {
      position: 'sticky', top: 0, zIndex: 100, background: B.surface,
      borderBottom: `1px solid ${B.border}`, display: 'flex', alignItems: 'center',
      padding: '8px 1.5rem', minHeight: 60, gap: '0.75rem', flexWrap: 'wrap',
    },
    backBtn: {
      background: 'none', border: 'none', cursor: 'pointer', color: B.muted,
      fontFamily: font, fontSize: '0.85rem', fontWeight: 600, display: 'flex',
      alignItems: 'center', gap: 5, padding: '4px 8px', borderRadius: 6,
    },
    title: { fontSize: '1rem', fontWeight: 700, color: B.text, margin: 0 },
    badge: {
      fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.04em',
      background: B.greenSoft, color: B.greenDark, padding: '3px 8px', borderRadius: 6,
    },
    lvlBadge: {
      fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.06em',
      background: B.purpleSoft, color: B.purple, padding: '3px 7px', borderRadius: 6,
    },
    body: { maxWidth: 820, margin: '0 auto', padding: '2rem 1.5rem 3rem' },
    card: {
      background: B.surface, border: `1px solid ${B.border}`, borderRadius: 14,
      padding: '1.25rem', marginBottom: '1rem',
    },
    sectionLabel: {
      fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.06em',
      color: B.faint, textTransform: 'uppercase', marginBottom: '0.6rem',
    },
    controlRow: { display: 'flex', gap: '1.6rem', flexWrap: 'wrap', alignItems: 'flex-end' },
    ctrlGroup: { display: 'flex', flexDirection: 'column', gap: 4 },
    ctrlLabel: {
      fontSize: '0.72rem', fontWeight: 700, color: B.muted,
      textTransform: 'uppercase', letterSpacing: '0.04em',
    },
    rangeHint: { fontSize: '0.72rem', color: B.faint },
    stat: { display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' },
    statNum: { fontSize: '1.35rem', fontWeight: 800, color: B.text, fontFamily: mono, lineHeight: 1 },
    statLabel: { fontSize: '0.66rem', fontWeight: 700, color: B.faint, textTransform: 'uppercase', letterSpacing: '0.04em' },
    btnRow: { display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginTop: '0.9rem' },
    btnPrimary: {
      fontFamily: font, fontSize: '0.88rem', fontWeight: 700, cursor: 'pointer',
      background: B.green, color: '#fff', border: 'none', borderRadius: 9,
      padding: '9px 16px', display: 'inline-flex', alignItems: 'center', gap: 7,
    },
    btnGhost: {
      fontFamily: font, fontSize: '0.88rem', fontWeight: 700, cursor: 'pointer',
      background: B.bg, color: B.text, border: `1.5px solid ${B.borderStrong}`,
      borderRadius: 9, padding: '9px 16px', display: 'inline-flex', alignItems: 'center', gap: 7,
    },
    expBtn: {
      width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      background: 'none', border: 'none', cursor: 'pointer', fontFamily: font,
      fontSize: '0.9rem', fontWeight: 600, color: B.text, padding: '0.9rem 1.25rem', borderRadius: 14,
    },
    expBody: { padding: '0 1.25rem 1.25rem', borderTop: `1px solid ${B.border}` },
    expGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginTop: '0.9rem' },
    expCard: { padding: '0.9rem', borderRadius: 8, border: `1px solid ${B.border}` },
    expCardTitle: { fontSize: '0.85rem', fontWeight: 700, color: B.text, marginBottom: '0.5rem' },
    expList: { fontSize: '0.8rem', color: B.muted, lineHeight: 1.65, paddingLeft: '1rem', margin: 0 },
    expNote: { fontSize: '0.78rem', color: B.faint, lineHeight: 1.6, marginTop: '0.75rem' },
  };
  const disabledStyle = { opacity: 0.45, cursor: 'not-allowed' };

  return (
    <div style={s.wrap}>
      {/* ── Topbar ─────────────────────────────────────────────────────── */}
      <header style={s.topbar}>
        {onBack && (
          <button style={s.backBtn} onClick={onBack} aria-label="Back">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor"
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="10 3 5 8 10 13" />
            </svg>
            Back
          </button>
        )}
        <a href="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', flexShrink: 0 }}>
          <img src="/logo.svg" alt="brookebit" height={40} style={{ display: 'block' }} />
        </a>
        <h1 style={s.title}>GAN Loop</h1>
        <span style={s.badge}>Case Study</span>
        <span style={s.lvlBadge}>HL</span>
      </header>

      {/* ── Body ───────────────────────────────────────────────────────── */}
      <main style={s.body}>

        {/* ── Loop diagram ───────────────────────────────────────────── */}
        <div style={s.card}>
          <div style={s.sectionLabel}>The adversarial loop</div>
          <LoopDiagram running={playing} reduceMotion={reduceMotion} />
          <p style={{ fontSize: '0.78rem', color: B.muted, marginTop: '0.4rem', lineHeight: 1.55 }}>
            The <strong>generator</strong> turns a <strong>D-dimensional noise vector</strong> into{' '}
            <strong>fake</strong> images. The <strong>discriminator</strong> compares them against{' '}
            <strong>real</strong> images and judges each <strong>real</strong> or <strong>fake</strong>.
            The generator never sees the real images; it improves only from the verdict.
          </p>
        </div>

        {/* ── Controls ───────────────────────────────────────────────── */}
        <div style={s.card}>
          <div style={s.sectionLabel}>Configuration</div>
          <div style={s.controlRow}>
            <div style={s.ctrlGroup}>
              <label style={s.ctrlLabel} htmlFor="gen-range">Generator strength</label>
              <input id="gen-range" type="range" min="0" max="100" step="1" value={gen}
                onChange={e => handleGen(+e.target.value)}
                style={{ accentColor: B.green, width: 180 }} />
              <span style={s.rangeHint}>{gen} / 100</span>
            </div>
            <div style={s.ctrlGroup}>
              <label style={s.ctrlLabel} htmlFor="disc-range">Discriminator strength</label>
              <input id="disc-range" type="range" min="0" max="100" step="1" value={disc}
                onChange={e => handleDisc(+e.target.value)}
                style={{ accentColor: B.purple, width: 180 }} />
              <span style={s.rangeHint}>{disc} / 100</span>
            </div>
            <div style={{ marginLeft: 'auto', ...s.stat }}>
              <span style={{ ...s.statNum, color: B.greenDark }}>{round}</span>
              <span style={s.statLabel}>Round</span>
            </div>
          </div>

          <div style={s.btnRow}>
            <button style={{ ...s.btnPrimary, ...(capped ? disabledStyle : {}) }}
              onClick={handleTrain} disabled={capped} aria-label="Train one round">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor"
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M13 8a5 5 0 1 1-1.5-3.5" /><polyline points="13 2 13 5 10 5" />
              </svg>
              Train one round
            </button>
            <button
              style={{ ...s.btnGhost, ...(reduceMotion || capped ? disabledStyle : {}) }}
              onClick={handlePlayPause} disabled={reduceMotion || capped}
              title={reduceMotion ? 'Auto-run is off because your system requests reduced motion. Use Train one round.' : undefined}
              aria-label={playing ? 'Pause auto-run' : 'Play auto-run'}>
              {playing ? (
                <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                  <rect x="3.5" y="3" width="3.2" height="10" rx="1" /><rect x="9.3" y="3" width="3.2" height="10" rx="1" />
                </svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                  <polygon points="4,3 13,8 4,13" />
                </svg>
              )}
              {playing ? 'Pause' : 'Play'}
            </button>
            <button style={s.btnGhost} onClick={handleReset} aria-label="Reset to a neutral start">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor"
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M3 8a5 5 0 1 1 1.5 3.5" /><polyline points="3 14 3 11 6 11" />
              </svg>
              Reset
            </button>
          </div>

          <p style={{ fontSize: '0.78rem', color: B.muted, marginTop: '0.85rem', lineHeight: 1.55 }}>
            <strong>Try it:</strong> keep the two strengths close and train, and realism and variety climb
            together. Push <strong>generator</strong> well above <strong>discriminator</strong> to trigger{' '}
            <strong>mode collapse</strong>; push the discriminator well above the generator to stall training.
          </p>
        </div>

        {/* ── Status + meters ────────────────────────────────────────── */}
        <div style={s.card}>
          <div style={{
            background: toneBg, border: `1px solid ${B.border}`, borderLeft: `4px solid ${toneColor}`,
            borderRadius: 8, padding: '0.7rem 0.9rem', marginBottom: '1rem',
          }} role="status" aria-live="polite">
            <div style={{ fontSize: '0.82rem', fontWeight: 800, color: toneColor }}>{status.title}</div>
            <div style={{ fontSize: '0.8rem', color: B.muted, lineHeight: 1.5, marginTop: 2 }}>{status.text}</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.2rem' }}>
            <Meter label="Realism" value={realism} color={B.greenDark} reduceMotion={reduceMotion}
              sub="How sharp and convincing the fakes look." />
            <Meter label="Variety" value={variety} color={B.blue} reduceMotion={reduceMotion}
              sub="How different the fakes are from each other." />
          </div>
        </div>

        {/* ── Fake thumbnails ────────────────────────────────────────── */}
        <div style={s.card}>
          <div style={s.sectionLabel}>Current fakes · round {round}</div>
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${N_THUMBS}, 1fr)`, gap: '0.6rem' }}>
            {fakes.map((f, i) => (
              <FakeThumb key={i} hue={f.hue} shape={f.shape} realism={realism} seed={f.seed} reduceMotion={reduceMotion} />
            ))}
          </div>
          <p style={{ fontSize: '0.74rem', color: B.faint, marginTop: '0.6rem', lineHeight: 1.5 }}>
            Each tile is one <strong>fake</strong> the generator produced this round. When variety is high
            they differ; under <strong>mode collapse</strong> they converge to near-identical images.
          </p>
        </div>

        {/* ── Explainer accordion ────────────────────────────────────── */}
        <div style={{ ...s.card, padding: 0, overflow: 'hidden' }}>
          <button style={s.expBtn} onClick={() => setHintOpen(o => !o)} aria-expanded={hintOpen}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke={B.green}
                strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M6 2h4M8 2v3M4.5 5.5A4 4 0 1 0 11.5 5.5" />
                <line x1="8" y1="9" x2="8" y2="12" />
                <circle cx="8" cy="13.5" r="0.5" fill={B.green} stroke="none" />
              </svg>
              How a GAN trains
            </span>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke={B.green}
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
              style={{ transform: hintOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
              <polyline points="3 6 8 11 13 6" />
            </svg>
          </button>

          {hintOpen && (
            <div style={s.expBody}>
              <p style={{ fontSize: '0.85rem', color: B.text, lineHeight: 1.6, marginTop: '0.9rem' }}>
                A <strong>GAN</strong> pits two networks against each other. The <strong>generator</strong>{' '}
                turns a <strong>D-dimensional noise vector</strong> into a candidate image; the{' '}
                <strong>discriminator</strong> looks at both <strong>real</strong> and <strong>fake</strong>{' '}
                images and guesses which is which. Each improves at the other's expense (the{' '}
                <strong>adversarial dynamic</strong>), and that competition is what drives sharp images.
              </p>

              <div style={s.expGrid}>
                <div style={s.expCard}>
                  <div style={s.expCardTitle}>Balance is the condition</div>
                  <ul style={s.expList}>
                    <li>The generator learns <strong>only from the verdict</strong>, never from the real images directly.</li>
                    <li>When the two are <strong>matched</strong>, realism and variety climb together.</li>
                    <li>Good results depend on keeping the pair in <strong>balance</strong>.</li>
                  </ul>
                </div>
                <div style={s.expCard}>
                  <div style={s.expCardTitle}>When it breaks</div>
                  <ul style={s.expList}>
                    <li><strong>Generator too strong</strong> → <strong>mode collapse</strong>: a few near-identical outputs, low variety (training still runs).</li>
                    <li><strong>Discriminator too strong</strong> → <strong>training stalled</strong>: the generator stops improving and realism plateaus.</li>
                  </ul>
                </div>
              </div>

              <p style={s.expNote}>
                Mitigations exist (for example rebalancing the two networks, or techniques that reward
                output diversity), but the idea to take away is that <strong>balance between generator and
                discriminator is the condition for realistic, varied results</strong>.
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
